import { type Ref, readonly, ref } from 'vue';

type ThemeMode = 'auto' | 'dark' | 'light';

let mediaQuery: MediaQueryList | undefined;
let mediaQueryHandler: ((e: MediaQueryListEvent) => void) | undefined;

const isDarkRef = ref(typeof document !== 'undefined' && document.body.classList.contains('body--dark'));

function setDark(shouldBeDark: boolean): void {
	if (typeof document === 'undefined') {
		return;
	}

	if (shouldBeDark) {
		document.body.classList.add('body--dark');
	} else {
		document.body.classList.remove('body--dark');
	}

	document.documentElement.style.colorScheme = shouldBeDark ? 'dark' : 'light';

	isDarkRef.value = shouldBeDark;
}

function resolveEffectiveDark(mode: ThemeMode): boolean {
	if (mode === 'dark') {
		return true;
	}
	if (mode === 'light') {
		return false;
	}
	if (typeof window !== 'undefined') {
		return window.matchMedia('(prefers-color-scheme: dark)').matches;
	}
	return false;
}

function cleanupMediaQuery(): void {
	if (mediaQuery && mediaQueryHandler) {
		mediaQuery.removeEventListener('change', mediaQueryHandler);
		mediaQuery = undefined;
		mediaQueryHandler = undefined;
	}
}

function setupMediaQueryWatcher(mode: ThemeMode): void {
	cleanupMediaQuery();

	if (mode !== 'auto' || typeof window === 'undefined') {
		return;
	}

	mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
	mediaQueryHandler = (e: MediaQueryListEvent) => {
		setDark(e.matches);
	};
	mediaQuery.addEventListener('change', mediaQueryHandler);
}

export function applyDarkMode(mode: ThemeMode): void {
	setDark(resolveEffectiveDark(mode));
	setupMediaQueryWatcher(mode);
}

interface DarkModeState {
	/** Apply dark mode (dark/light/auto) */
	applyDarkMode: (mode: ThemeMode) => void;
	/** Reactive boolean — true when dark mode is active */
	isDark: Readonly<Ref<boolean>>;
}

/**
 * Dark mode composable — PrimeVue-native replacement for Quasar Dark.
 * Manages .body--dark class directly on <body>.
 */
export function useDarkMode(): DarkModeState {
	return {
		applyDarkMode,
		isDark: readonly(isDarkRef),
	};
}
